import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { PopupDialog } from '../../../../components/common';
import { TextFieldComponent } from '../../../../components/common/TextFieldComponent';
import { ProcedenciaSelect } from '../../../../components/parametros';
import { type AsociacionEditDialogProps, type AsociacionResponse, type AsociacionRequest } from '../types/asociacion';
import { useAsociacionForm } from '../hooks';
import { ASOCIACION_CONSTANTS } from '../../../../constants/asociacion';
import { AsociacionService } from '../services';

// Helper function to convert AsociacionResponse to AsociacionRequest
const convertToAsociacionRequest = (response: AsociacionResponse): AsociacionRequest => ({
  idAsociacion: response.idAsociacion,
  descripcion: response.descripcion,
  idProcedencia: response.idProcedencia,
});

/**
 * EditAsociacion Component
 *
 * A comprehensive asociacion editing dialog that combines form fields and dialog functionality.
 * Features:
 * - Complete asociacion information form
 * - Validation and error handling
 * - Loading states and submission
 * - Responsive layout design
 * - Clean architecture with separated concerns
 */
const EditAsociacion: React.FC<AsociacionEditDialogProps> = ({
  open,
  asociacionId,
  onClose,
  onSave,
  title = ASOCIACION_CONSTANTS.DIALOG_CONFIG.DEFAULT_TITLE,
}) => {

  // Local state for asociacion data
  const [, setAsociacionData] = useState<AsociacionResponse | null>(null);
  const [loadingAsociacion, setLoadingAsociacion] = useState(false);
  const [initialAsociacionRequest, setInitialAsociacionRequest] = useState<AsociacionRequest | null>(null);

  const {
    formData,
    errors,
    isValid,
    isDirty,
    isSubmitting,
    handleFieldChange,
    handleSubmit,
  } = useAsociacionForm({
    initialAsociacionRequest: initialAsociacionRequest,
    isOpen: open,
    onSubmit: onSave,
  });

  // Fetch asociacion data when dialog opens
  useEffect(() => {
    const fetchAsociacion = async () => {
      if (!open || !asociacionId) {
        setInitialAsociacionRequest(null);
        setAsociacionData(null);
        return;
      }

      try {
        setLoadingAsociacion(true);
        const data = await AsociacionService.getAsociacionById(asociacionId);
        setAsociacionData(data);
        setInitialAsociacionRequest(convertToAsociacionRequest(data));
      } catch (error) {
        console.error('Error fetching asociacion:', error);
        setAsociacionData(null);
        setInitialAsociacionRequest(null);
      } finally {
        setLoadingAsociacion(false);
      }
    };

    fetchAsociacion();
  }, [open, asociacionId]);

  // Handle new asociacion case (no asociacionId)
  useEffect(() => {
    if (open && !asociacionId) {
      setInitialAsociacionRequest(null);
      setAsociacionData(null);
    }
  }, [open, asociacionId]);

  const handleCancelClick = (): void => {
    if (isDirty) {
      // In a real application, you might want to show a confirmation dialog
      // before discarding changes
    }
    onClose();
  };

  // Early return if no form data or still loading
  if (!formData || (asociacionId && loadingAsociacion)) {
    return null;
  }

  const dialogTitle = `${title}${isDirty ? ' *' : ''}`;
  const { FIELD_LABELS } = ASOCIACION_CONSTANTS;


  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <PopupDialog
        open={open}
        onClose={handleCancelClick}
        title={dialogTitle}
        maxWidth={ASOCIACION_CONSTANTS.DIALOG_CONFIG.MAX_WIDTH}
        disableEscapeKeyDown={isDirty}
        primaryAction={{
          label: 'Guardar',
          onClick: handleSubmit,
          disabled: !isValid,
          loading: isSubmitting,
        }}
        secondaryAction={{
          label: 'Cancelar',
          onClick: handleCancelClick,
        }}
      >
        <Box sx={{ mt: 2 }}>
          {/* Row 1: Descripción */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 100%' }}>
              <TextFieldComponent
                label={FIELD_LABELS.descripcion}
                value={formData.descripcion}
                onChange={(value: string) => handleFieldChange('descripcion', value)}
                error={errors.descripcion}
              />
            </Box>
          </Box>

          {/* Row 2: Procedencia */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 250px' }}>
              <ProcedenciaSelect
                label={FIELD_LABELS.idProcedencia}
                value={formData.idProcedencia || ''}
                onChange={(value: string | number) => handleFieldChange('idProcedencia', value)}
                error={errors.idProcedencia}
              />
            </Box>
          </Box>

        </Box>
      </PopupDialog>
    </LocalizationProvider>
  );
};

export default EditAsociacion;